import React, { useMemo } from "react";
import {
  CRow, CCol, CCard, CCardHeader, CCardBody,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CBadge, CButton
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilMediaPlay, cilMap, cilSpeedometer, cilClock } from "@coreui/icons";
import { ResponsiveBar } from "@nivo/bar";
import dayjs from "dayjs";
import TruckMap from "./TruckMap";
import { getFakeLocalDate } from "../utils/timeUtils";

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return "0м 0с";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м ${s}с`;
};

const LEGEND_ITEMS = [
  { color: "#ff0000", label: "Погрузка мешка (Приоритет)" },
  { color: "#ff9500", label: "Погрузка ведра (Приоритет)" },
  { color: "#8a93a2", label: "Стоянка (0 км/ч)" },
  { color: "#2eb85c", label: "Движение 0–20 км/ч (Разметка)" },
  { color: "#3b82f6", label: "Движение 20–40 км/ч" },
  { color: "#6f42c1", label: "Движение > 40 км/ч" },
];

const RoadMarkingDashboard = ({
  data,
  trajectoryData = [],
  loadMapPoints = [],
  mapViewRef,
  timezoneOffset = 0,
  onVideoJump,
  timelineComponent,
}) => {
  const kpis = data?.kpis || {};
  const sessions = data?.sessions_summary || [];
  const hourlyChart = data?.hourly_chart || [];

  const speedBalanceData = useMemo(() => [
    {
      label: "Погрузка сырья",
      value: Number(((kpis.time_loading_sec || 0) / 60).toFixed(1)),
      color: "#ff0000",
    },
    {
      label: "0 км/ч (Стоянка)",
      value: Number(((kpis.time_stop_sec || 0) / 60).toFixed(1)),
      color: "#8a93a2",
    },
    {
      label: "0–20 км/ч (Работа)",
      value: Number(((kpis.time_speed_0_20_sec || 0) / 60).toFixed(1)),
      color: "#2eb85c",
    },
    {
      label: "20–40 км/ч",
      value: Number(((kpis.time_speed_20_40_sec || 0) / 60).toFixed(1)),
      color: "#3b82f6",
    },
    {
      label: "> 40 км/ч",
      value: Number(((kpis.time_speed_40_plus_sec || 0) / 60).toFixed(1)),
      color: "#6f42c1",
    },
  ], [kpis]);

  return (
    <div style={{ background: "white", padding: "10px" }}>
      {/* 1. ПЛИТКИ KPI (2 РЯДА ПО 4 КАРТОЧКИ) */}
      <CRow className="g-3 mb-4">
        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Погружено мешков</div>
              <div className="fs-3 fw-bold" style={{ color: "#ff0000" }}>
                {kpis.total_bags || 0} <span className="fs-6">шт</span>
              </div>
              <div className="small text-muted mt-1">
                Ср. погрузка: <b>{kpis.avg_bag_duration_sec || 0} сек</b>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Погружено вёдер</div>
              <div className="fs-3 fw-bold" style={{ color: "#ff9500" }}>
                {kpis.total_buckets || 0} <span className="fs-6">шт</span>
              </div>
              <div className="small text-muted mt-1">
                Ср. погрузка: <b>{kpis.avg_bucket_duration_sec || 0} сек</b>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Всего тары / Сессий</div>
              <div className="fs-3 fw-bold text-primary">
                {kpis.total_items || 0} <span className="fs-6 text-muted">/ {kpis.total_sessions || 0} сес.</span>
              </div>
              <div className="small text-muted mt-1">
                Темп: <b>{kpis.items_per_hour || 0} ед/ч</b>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Пробег и Скорость</div>
              <div className="fs-3 fw-bold text-info">
                {kpis.total_mileage_km || 0} <span className="fs-6">км</span>
              </div>
              <div className="small text-muted mt-1">
                Ср: <b>{kpis.avg_speed_kmh || 0}</b> км/ч · Макс: <b>{kpis.max_speed_kmh || 0}</b> км/ч
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Общее время погрузки</div>
              <div className="fs-4 fw-bold text-danger">{formatDuration(kpis.time_loading_sec)}</div>
              <div className="small text-muted mt-1">Приоритет на шкале</div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Рабочий ход (0–20 км/ч)</div>
              <div className="fs-4 fw-bold text-success">{formatDuration(kpis.time_speed_0_20_sec)}</div>
              <div className="small text-muted mt-1">Нанесение разметки</div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Перегон (&gt; 20 км/ч)</div>
              <div className="fs-4 fw-bold text-primary">
                {formatDuration((kpis.time_speed_20_40_sec || 0) + (kpis.time_speed_40_plus_sec || 0))}
              </div>
              <div className="small text-muted mt-1">
                20–40: {formatDuration(kpis.time_speed_20_40_sec)} · &gt;40: {formatDuration(kpis.time_speed_40_plus_sec)}
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={6} md={3}>
          <CCard className="h-100 shadow-sm border-0 bg-light">
            <CCardBody className="text-center p-3">
              <div className="text-muted small text-uppercase fw-bold mb-1">Стоянка (0 км/ч)</div>
              <div className="fs-4 fw-bold text-secondary">{formatDuration(kpis.time_stop_sec)}</div>
              <div className="small text-muted mt-1">Ожидание без погрузки</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* 2. ЛЕГЕНДА ЦВЕТОВ СКОРОСТИ И ПОГРУЗКИ + ТАЙМЛАЙН */}
      <div className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 mb-2 bg-light rounded border small">
        <span className="fw-bold text-dark">Шкала статусов и скоростей:</span>
        {LEGEND_ITEMS.map((it, idx) => (
          <span key={idx} className="d-inline-flex align-items-center gap-1 fw-semibold text-dark">
            <span
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "3px",
                backgroundColor: it.color,
                display: "inline-block",
              }}
            />
            {it.label}
          </span>
        ))}
      </div>

      <div className="mb-4">{timelineComponent}</div>

      {/* 3. ГРАФИКИ АНАЛИТИКИ: ПОЧАСОВАЯ ЗАГРУЗКА И БАЛАНС ВРЕМЕНИ ПО СКОРОСТЯМ */}
      <CRow className="g-3 mb-4">
        <CCol xs={12} lg={7}>
          <CCard className="shadow-sm border-0 h-100">
            <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
              <span><CIcon icon={cilClock} className="me-2 text-primary" />Динамика загрузки сырья по часам</span>
              <span className="small text-muted">Мешки и Вёдра (шт)</span>
            </CCardHeader>
            <CCardBody style={{ height: "300px" }}>
              {hourlyChart.length > 0 ? (
                <ResponsiveBar
                  data={hourlyChart}
                  keys={["Мешки", "Вёдра"]}
                  indexBy="time"
                  margin={{ top: 20, right: 30, bottom: 60, left: 50 }}
                  padding={0.3}
                  colors={["#ff0000", "#ff9500"]}
                  axisBottom={{ tickRotation: -35 }}
                  axisLeft={{ legend: "Количество (шт)", legendPosition: "middle", legendOffset: -40 }}
                  labelTextColor="#ffffff"
                />
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  Нет почасовых данных о погрузке за выбранный период
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12} lg={5}>
          <CCard className="shadow-sm border-0 h-100">
            <CCardHeader className="bg-white fw-bold">
              <CIcon icon={cilSpeedometer} className="me-2 text-success" />
              Распределение времени по режимам (мин)
            </CCardHeader>
            <CCardBody style={{ height: "300px" }}>
              <ResponsiveBar
                data={speedBalanceData}
                keys={["value"]}
                indexBy="label"
                margin={{ top: 20, right: 20, bottom: 60, left: 55 }}
                padding={0.35}
                colors={({ data: d }) => d.color}
                colorBy="indexValue"
                axisBottom={{ tickRotation: -20 }}
                axisLeft={{ legend: "Минуты", legendPosition: "middle", legendOffset: -45 }}
                labelTextColor="#ffffff"
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* 4. СГЛАЖЕННАЯ КАРТА МАРШРУТА И СКОРОСТЕЙ */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardHeader className="bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
          <span>
            <CIcon icon={cilMap} className="me-2 text-primary" />
            Карта маршрута, сглаженная скорость и точки загрузки сырья
          </span>
          <span className="small text-muted">Клик по точке загрузки — переход к видео</span>
        </CCardHeader>
        <CCardBody className="p-2">
          <TruckMap
            track={trajectoryData}
            loadPoints={loadMapPoints}
            unloadPoints={[]}
            height="480px"
            viewState={mapViewRef}
            onPointClick={({ exactTime }) => onVideoJump(exactTime)}
          />
        </CCardBody>
      </CCard>

      {/* 5. ЖУРНАЛ ПОГРУЗКИ (Мешки / Вёдра + среднее время) */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardHeader className="bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
          <span>Журнал сессий погрузки (Мешки / Вёдра)</span>
          <CBadge color="secondary" shape="rounded-pill">{sessions.length} записей</CBadge>
        </CCardHeader>
        <CCardBody className="p-0">
          <div className="table-responsive" style={{ maxHeight: "55vh", overflowY: "auto" }}>
            <CTable hover align="middle" className="mb-0" small>
              <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <CTableRow className="text-nowrap">
                  <CTableHeaderCell className="text-center ps-3">#</CTableHeaderCell>
                  <CTableHeaderCell>Время</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Длительность</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Мешки (шт / ср. время)</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Вёдра (шт / ср. время)</CTableHeaderCell>
                  <CTableHeaderCell className="text-center">Фото</CTableHeaderCell>
                  <CTableHeaderCell className="text-center pe-3">Видео</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {sessions.map((s, idx) => {
                  const fakeStart = getFakeLocalDate(s.start, timezoneOffset);
                  const fakeEnd = getFakeLocalDate(s.end, timezoneOffset);
                  return (
                    <CTableRow key={s.id}>
                      <CTableDataCell className="text-center ps-3 fw-bold text-muted">{idx + 1}</CTableDataCell>
                      <CTableDataCell>
                        <div className="fw-bold">{dayjs(fakeStart).format("DD.MM.YYYY")}</div>
                        <div className="small text-muted">
                          {dayjs(fakeStart).format("HH:mm:ss")} — {dayjs(fakeEnd).format("HH:mm:ss")}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell className="text-center fw-semibold">{formatDuration(s.duration_sec)}</CTableDataCell>
                      <CTableDataCell className="text-center">
                        <CBadge style={{ backgroundColor: "#ff0000" }} className="px-2 py-1 fs-6">
                          {s.bags_count} шт
                        </CBadge>
                        {s.avg_bag_sec > 0 && (
                          <div className="small text-muted mt-1">~{s.avg_bag_sec} с/мешок</div>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-center">
                        <CBadge style={{ backgroundColor: "#ff9500" }} className="px-2 py-1 fs-6">
                          {s.buckets_count} шт
                        </CBadge>
                        {s.avg_bucket_sec > 0 && (
                          <div className="small text-muted mt-1">~{s.avg_bucket_sec} с/ведро</div>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-center">
                        {s.photo_url ? (
                          <img
                            src={s.photo_url}
                            alt="Кадр"
                            style={{ width: "52px", height: "38px", objectFit: "cover", borderRadius: "4px" }}
                          />
                        ) : "—"}
                      </CTableDataCell>
                      <CTableDataCell className="text-center pe-3">
                        <CButton
                          size="sm"
                          color="primary"
                          variant="outline"
                          className="fw-bold"
                          onClick={() => onVideoJump(fakeStart.getTime())}
                        >
                          <CIcon icon={cilMediaPlay} className="me-1" /> Смотреть
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })}
                {sessions.length === 0 && (
                  <CTableRow>
                    <CTableDataCell colSpan={7} className="text-center p-5 text-muted">
                      Погрузок за выбранный период не найдено
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default RoadMarkingDashboard;